import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, Select, MenuItem, FormControl, InputLabel, Alert, Grid, Chip } from '@mui/material';
import api from '../api';

const categories = ['General', 'Attendance', 'Academic', 'Billing', 'Other'];

const ParentTickets: React.FC = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [category, setCategory] = useState('General');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);

  const loadTickets = async () => {
    try {
      const res = await api.get('/parent-tickets');
      setTickets(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load tickets');
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleCreate = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('subject', subject);
      formData.append('message', message);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      await api.post('/parent-tickets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Ticket submitted successfully.');
      setMessage('');
      setSubject('');
      setAttachment(null);
      await loadTickets();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Parent Support Tickets
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Submit a new ticket
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="parent-ticket-category-label">Category</InputLabel>
              <Select
                labelId="parent-ticket-category-label"
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                {categories.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" component="label">
              Attach file (optional)
              <input
                hidden
                type="file"
                onChange={(e) => setAttachment(e.target.files ? e.target.files[0] : null)}
              />
            </Button>
            {attachment && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Attached: {attachment.name}
              </Typography>
            )}
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleCreate} disabled={loading || !message.trim()}>
              {loading ? 'Submitting...' : 'Submit ticket'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Your tickets
      </Typography>
      {tickets.length === 0 ? (
        <Typography>No tickets have been submitted yet.</Typography>
      ) : (
        tickets.map((ticket) => (
          <Paper key={ticket.ticketId} sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1">{ticket.subject || 'Parent ticket'}</Typography>
              <Chip label={ticket.status} color={ticket.status === 'open' ? 'success' : ticket.status === 'pending' ? 'warning' : ticket.status === 'resolved' || ticket.status === 'closed' ? 'default' : 'info'} />
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Ticket ID: {ticket.ticketId}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>Category: {ticket.category}</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{ticket.message}</Typography>
            {ticket.responses?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Responses</Typography>
                {ticket.responses.map((response: any, index: number) => (
                  <Paper key={`${ticket.ticketId}-resp-${index}`} sx={{ p: 2, mb: 1, backgroundColor: '#ffffff', color: '#000000 !important' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#000000 !important' }}>{response.from === 'admin' ? 'School response' : 'Parent response'}</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: '#000000 !important' }}>{response.message || 'No response text provided.'}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{new Date(response.createdAt).toLocaleString()}</Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
};

export default ParentTickets;
